import { useEffect, useRef, useState } from 'react'
import { motion, useInView, useReducedMotion } from 'motion/react'
import { Check, ShieldCheck } from 'lucide-react'
import { InteractiveDots } from '@/components/motion'
import { OfficeAppIcon } from '@/components/landing/OfficeAppIcon'
import type { IndustryData } from '@/data/industries'
import { cn } from '@/lib/utils'
import { lowerFirst, shortTime } from './listing'
import { stepAgent } from './WorkflowDiagram'

const STEP_MS = 2800
const EASE = [0.22, 1, 0.36, 1] as const

function Token({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-block bg-[#e9e7ff] px-1.5 py-0.5 font-[family-name:var(--font-landing-mono)] text-[0.7em] font-semibold text-[var(--color-landing-accent)]">
      {children}
    </span>
  )
}

/** A PHI field: the token, covered by a black redaction bar once the compliance step has run. */
function PhiField({ token, redacted }: { token: string; redacted: boolean }) {
  return (
    <span className="relative inline-block">
      <Token>{token}</Token>
      <motion.span
        className="absolute inset-0 flex items-center justify-end bg-[#0a0a0a] pr-1.5 text-[0.6em] font-bold uppercase tracking-[0.08em] text-white"
        initial={false}
        animate={{ clipPath: redacted ? 'inset(0% 0% 0% 0%)' : 'inset(0% 100% 0% 0%)' }}
        transition={{ duration: 0.6, ease: EASE }}
      >
        PHI
      </motion.span>
    </span>
  )
}

const line = 'block h-[5px] bg-[var(--color-landing-gray-200)]'

/**
 * The about page's 16:9 media slot, as an animated clinical form: the
 * industry's first workflow (discharge documentation) runs step by step on a
 * discharge-summary document in Word. Formatting fills the fields, the
 * compliance pass redacts the PHI fields, the medication review ticks the
 * list and the patient instructions appear in plain language. Pauses
 * off-screen; shows the finished document under reduced motion.
 */
export function ClinicalFormMedia({ industry }: { industry: IndustryData }) {
  const workflow = industry.workflows[0]!
  const steps = workflow.steps.slice(0, 4)
  const template = industry.templates[0]
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { amount: 0.35 })
  const reduce = useReducedMotion()
  const [step, setStep] = useState(0)

  useEffect(() => {
    if (reduce || !inView) return
    const id = window.setInterval(() => setStep((s) => (s + 1) % steps.length), STEP_MS)
    return () => window.clearInterval(id)
  }, [inView, reduce, steps.length])

  const at = reduce ? steps.length - 1 : step
  const redacted = at >= 1
  const medsChecked = at >= 2
  const instructions = at >= 3

  return (
    <section aria-labelledby="clinical-media-title" className="landing-container">
      <div ref={ref} className="relative overflow-hidden bg-[var(--color-landing-accent)] xl:aspect-video">
        <InteractiveDots />
        <div className="relative z-10 grid h-full gap-8 p-5 sm:p-8 lg:grid-cols-[minmax(0,0.32fr)_minmax(0,1fr)] lg:items-center lg:gap-12 lg:p-[4%]">
          {/* The workflow in words, the active step in full white */}
          <div className="order-2 lg:order-1">
            <h2
              id="clinical-media-title"
              className="font-[family-name:var(--font-landing-display)] text-[1.6rem] uppercase leading-[1.4] tracking-[-0.02em] text-white"
            >
              One discharge, {steps.length} steps.
            </h2>
            <p className="mt-4 max-w-[36ch] landing-text-body-sm text-white/85">
              {workflow.name}: from “{lowerFirst(workflow.trigger)}” to a {lowerFirst(workflow.output)}, in{' '}
              {shortTime(workflow.timeSaved)}.
            </p>
            <ol className="mt-8 grid gap-3 border-t border-white/25 pt-6">
              {steps.map((label, i) => (
                <li
                  key={label}
                  aria-current={i === at ? 'step' : undefined}
                  className={cn(
                    'flex items-baseline gap-3 text-base font-semibold transition-colors duration-300',
                    i === at ? 'text-white' : 'text-white/75'
                  )}
                >
                  <span
                    aria-hidden
                    className={cn('h-2 w-2 shrink-0 self-center bg-[#ff00f2] transition-opacity duration-300', i === at ? 'opacity-100' : 'opacity-0')}
                  />
                  <span aria-hidden className="font-[family-name:var(--font-landing-mono)] text-xs tabular-nums">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  {label}
                </li>
              ))}
            </ol>
          </div>

          {/* Word window with the discharge summary and a Templatean pane */}
          <div aria-hidden className="order-1 border border-black/10 bg-white text-[#0a0a0a] shadow-[0_40px_80px_-30px_rgb(5_0_90_/_0.6)] lg:order-2">
            <div className="flex h-9 items-center justify-between gap-4 border-b border-[var(--color-landing-gray-200)] bg-[var(--color-landing-gray-50)] px-3">
              <span className="flex min-w-0 items-center gap-2 text-xs font-semibold text-[var(--color-landing-gray-700)]">
                <OfficeAppIcon app="word" className="h-4 w-4 shrink-0" />
                <span className="truncate">{template?.name ?? workflow.output}.docx</span>
              </span>
              <span className="font-[family-name:var(--font-landing-mono)] text-[11px] tabular-nums text-[var(--color-landing-gray-700)]">
                Step {at + 1} / {steps.length}
              </span>
            </div>

            <div className="grid sm:grid-cols-[minmax(0,1fr)_minmax(0,13rem)]">
              {/* Document */}
              <div className="flex flex-col gap-4 p-5 text-[13px] sm:p-7 md:text-sm">
                <div className="flex items-end justify-between gap-4 border-b-2 border-[#0a0a0a] pb-3">
                  <span className="font-[family-name:var(--font-landing-display)] text-base uppercase leading-[1.3] md:text-lg">
                    {template?.name ?? 'Discharge summary'}
                  </span>
                  <motion.span
                    className="flex items-center gap-1 bg-[#00d722] px-2 py-1 text-[10px] font-bold uppercase tracking-[0.06em] text-[#0a0a0a]"
                    initial={false}
                    animate={{ opacity: redacted ? 1 : 0, y: redacted ? 0 : 6 }}
                    transition={{ duration: 0.4, ease: EASE }}
                  >
                    <ShieldCheck className="h-3.5 w-3.5" /> HIPAA check
                  </motion.span>
                </div>

                <dl className="grid grid-cols-[7.5rem_minmax(0,1fr)] items-center gap-x-4 gap-y-3 md:grid-cols-[9rem_minmax(0,1fr)]">
                  {[
                    ['Patient', <PhiField key="p" token="{{patient_name}}" redacted={redacted} />],
                    ['Date of birth', <PhiField key="d" token="{{date_of_birth}}" redacted={redacted} />],
                    ['Diagnosis', <Token key="c">{'{{icd10_code}}'}</Token>],
                    ['Care plan', <span key="l" className={cn(line, 'w-full')} />],
                  ].map(([label, value]) => (
                    <div key={label as string} className="contents">
                      <dt className="text-[11px] font-bold uppercase tracking-[0.06em] text-[var(--color-landing-gray-500)]">{label}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>

                <div className="border border-[var(--color-landing-gray-200)] p-3">
                  <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-[var(--color-landing-gray-500)]">Medications</p>
                  <ul className="grid gap-2">
                    {[78, 64, 86].map((w, i) => (
                      <li key={w} className="flex items-center gap-2">
                        <motion.span
                          className="flex h-4 w-4 shrink-0 items-center justify-center text-white"
                          initial={false}
                          animate={{ opacity: medsChecked ? 1 : 0.15, scale: medsChecked ? 1 : 0.7 }}
                          transition={{ duration: 0.35, ease: EASE, delay: medsChecked ? i * 0.12 : 0 }}
                          style={{ backgroundColor: 'var(--color-landing-accent)' }}
                        >
                          <Check className="h-3 w-3" strokeWidth={3} />
                        </motion.span>
                        <span className={line} style={{ width: `${w}%` }} />
                      </li>
                    ))}
                  </ul>
                </div>

                <motion.div
                  className="bg-[#fff0fe] p-3 dark:bg-[#fff0fe]"
                  initial={false}
                  animate={{ clipPath: instructions ? 'inset(0% 0% 0% 0%)' : 'inset(0% 0% 100% 0%)' }}
                  transition={{ duration: 0.6, ease: EASE }}
                >
                  <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-[#8a0083]">Patient instructions</p>
                  {[96, 88, 70].map((w) => (
                    <span key={w} className="mt-1.5 block h-[5px] bg-[#ffb8fb]" style={{ width: `${w}%` }} />
                  ))}
                </motion.div>
              </div>

              {/* Agent pane */}
              <div className="hidden flex-col gap-2 border-l border-[var(--color-landing-gray-200)] bg-[var(--color-landing-gray-50)] p-3 sm:flex">
                <span className="mb-1 text-xs font-bold tracking-[-0.01em]">
                  templ<span className="text-[var(--color-landing-accent)]">a</span>tean
                </span>
                {steps.map((label, i) => {
                  const agent = stepAgent(label, industry.aiAgents)
                  const done = i < at
                  const current = i === at
                  return (
                    <div
                      key={label}
                      className={cn(
                        'flex items-center gap-2 border bg-white p-2 transition-colors duration-300',
                        current ? 'border-[var(--color-landing-accent)]' : 'border-[var(--color-landing-gray-200)]'
                      )}
                    >
                      <span
                        className="flex h-6 w-6 shrink-0 items-center justify-center font-[family-name:var(--font-landing-display)] text-[10px] text-white"
                        style={{ backgroundColor: done ? '#00b81d' : current ? 'var(--color-landing-accent)' : '#0a0a0a' }}
                      >
                        {done ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : agent ? agent.name[0] : i + 1}
                      </span>
                      <span className="min-w-0 text-[11px] font-semibold leading-tight text-[var(--color-landing-gray-800)]">
                        {agent?.name ?? 'Step'}
                        <span className="block font-normal text-[var(--color-landing-gray-500)]">{agent?.role ?? label}</span>
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
